import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, collection, query, where, getDocs, arrayUnion } from "firebase/firestore";
import { db } from "../firebase/config";

/** provider 이름 정규화 */
function normalizeProvider(raw) {
  if (!raw) return "unknown";
  const p = raw.toLowerCase();
  if (p.includes("google")) return "google";
  if (p.includes("kakao")) return "kakao";
  if (p.includes("email") || p.includes("password")) return "email";
  return p;
}

/**
 * 4단계 조회: authUid → linkedSocialUid → linkedEmailUid → 문서ID 직접
 */
export async function getUserProfileByUid(uid) {
  // 1) authUid 필드로 조회 (관리자가 생성한 계정 — loginId 로그인)
  const q = query(collection(db, "users"), where("authUid", "==", uid));
  const qSnap = await getDocs(q);
  if (!qSnap.empty) {
    const d = qSnap.docs[0];
    return { uid: d.id, ...d.data() };
  }

  // 2) linkedSocialUid로 조회 (소셜 → 기존 계정 연결)
  const q2 = query(collection(db, "users"), where("linkedSocialUid", "==", uid));
  const q2Snap = await getDocs(q2);
  if (!q2Snap.empty) {
    const d = q2Snap.docs[0];
    return { uid: d.id, ...d.data() };
  }

  // 3) linkedEmailUid로 조회 (이메일 → 기존 계정 연결)
  const q3 = query(collection(db, "users"), where("linkedEmailUid", "==", uid));
  const q3Snap = await getDocs(q3);
  if (!q3Snap.empty) {
    const d = q3Snap.docs[0];
    return { uid: d.id, ...d.data() };
  }

  // 4) users/{uid} 직접 조회 (소셜 로그인 / 회원가입 계정)
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return { uid, ...snap.data() };

  return null;
}

export async function getUserByPhone(phoneE164) {
  const phoneRef = doc(db, "phones", phoneE164);
  const phoneSnap = await getDoc(phoneRef);
  if (!phoneSnap.exists()) return null;
  const { primaryUid } = phoneSnap.data();
  if (!primaryUid) return null;
  return getUserProfileByUid(primaryUid);
}

/**
 * 빈 문서 생성 (소셜/이메일 최초 가입 시)
 * providers 배열 포함
 */
export async function initUserDoc({ uid, email, provider }) {
  const norm = normalizeProvider(provider);
  const ref = doc(db, "users", uid);
  await setDoc(ref, {
    email: email || "",
    name: "",
    role: "",
    providers: [norm],
    provider: norm,
    phoneE164: "",
    phoneVerified: false,
    status: "",
    createdAt: serverTimestamp(),
  });
}

/**
 * 소셜 계정을 기존 계정에 연결 + 소셜 빈 문서 삭제
 */
export async function linkSocialToExistingUser({ existingUid, socialUid, provider }) {
  const norm = normalizeProvider(provider);
  const userRef = doc(db, "users", existingUid);

  const updates = {
    linkedSocialUid: socialUid,
    updatedAt: serverTimestamp(),
  };
  if (norm) {
    updates.providers = arrayUnion(norm);
  }

  await setDoc(userRef, updates, { merge: true });

  // 소셜 빈 문서 삭제 (이름/역할 없는 임시 문서)
  try {
    const socialRef = doc(db, "users", socialUid);
    const socialSnap = await getDoc(socialRef);
    if (socialSnap.exists()) {
      const data = socialSnap.data();
      if (!data.name && !data.role) {
        await deleteDoc(socialRef);
      }
    }
  } catch (e) {
    console.warn("소셜 빈 문서 삭제 실패 (무시):", e.message);
  }
}

/** 전화번호 연결 — users 필수 저장, phones/users_by_phone 은 선택 */
export async function linkPhoneToUid({ uid, phoneE164 }) {
  // E164 → 표시용 번호 변환 (010-1234-5678)
  const local = phoneE164.replace(/^\+82/, "0");
  const phone = local.length === 11
    ? `${local.slice(0,3)}-${local.slice(3,7)}-${local.slice(7)}`
    : local;

  // users 문서는 필수 — 실패 시 에러
  const userRef = doc(db, "users", uid);
  await setDoc(userRef, { phoneE164, phone, phoneVerified: true, updatedAt: serverTimestamp() }, { merge: true });

  // phones, users_by_phone 인덱스는 실패해도 핵심 흐름 차단하지 않음
  try {
    const phoneRef = doc(db, "phones", phoneE164);
    await setDoc(phoneRef, { primaryUid: uid, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    console.warn("phones 인덱스 저장 실패 (무시):", e.message);
  }
  try {
    const indexRef = doc(db, "users_by_phone", phoneE164);
    await setDoc(indexRef, { uid, phoneE164, updatedAt: serverTimestamp() }, { merge: true });
  } catch (e) {
    console.warn("users_by_phone 인덱스 저장 실패 (무시):", e.message);
  }
}

/** users_by_phone 인덱스 문서 관리 (단독 사용 시) */
export async function upsertUserPhoneIndex({ uid, phoneE164 }) {
  const indexRef = doc(db, "users_by_phone", phoneE164);
  await setDoc(indexRef, { uid, phoneE164, updatedAt: serverTimestamp() }, { merge: true });
}
